import { Button, Center, Loader, Paper, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import axios, { AxiosError } from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { ScreenContainer } from 'components/ScreenContainer/ScreenContainer';
import { useQuery } from '@tanstack/react-query';
import { ErrorNotification, ErrorNotificationType, isValidErrorNotificationType } from 'components/ErrorNotification/ErrorNotification';
import { IconInfo } from 'resources/icons/Info';
import { globalTheme } from 'styles/globalStyles';

type PriceData = {
  data: {
    unit_amount: number;
    currency: string;
    error?: string;
  };
};

// TODO: Load locale from config service instead of relying on currency
function currencyToIntlLocale(currency: string) {
  switch (currency) {
    case 'usd':
      return 'en-US';
    case 'eur':
      return 'de-DE';
    case 'nok':
      return 'nb-NO';
    default:
      return 'en-US';
  }
}

type SpecificAxiosError = AxiosError<
  {
    error: string;
  },
  any
>;

export const PaymentPage = () => {
  const {
    other: { spacing },
    colors,
  } = useMantineTheme();
  const { t } = useTranslation();
  const { state } = useParams();
  const [searchParams] = useSearchParams();
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<AxiosError<
    {
      error: string;
    },
    any
  > | null>(null);

  const [paymentError, setPaymentError] = useState<ErrorNotificationType>();

  const paymentStatus = searchParams.get('payment');
  useEffect(() => {
    if (paymentStatus === 'cancelled') {
      setPaymentError(ErrorNotificationType.PaymentCancelled);
    }
  }, [paymentStatus]);

  const { data, isLoading, error: priceError } = useQuery<PriceData, SpecificAxiosError>({ queryKey: ['price'], queryFn: () => axios.get(`/api/payment/pricing?state=${state}`) });
  const { data: price } = data || {};

  useEffect(() => {
    const beginCheckout = async () => {
      try {
        let path = '/api/payment/checkout';
        const stripeCustomerId = searchParams.get('stripe_customer_id');
        let queryParams = new URLSearchParams();
        if (stripeCustomerId) {
          queryParams.append('stripe_customer_id', stripeCustomerId);
        }
        if (searchParams.get('new_window')) {
          queryParams.append('new_window', 'true');
        }
        if (queryParams.toString()) {
          path = path + '?' + queryParams.toString();
        }
        const result = await axios.post(path, {
          state,
        });
        setCheckoutUrl(result.data.url);
      } catch (error) {
        setCheckoutError(error as SpecificAxiosError);
        setCheckoutUrl(null);
      }
    };
    beginCheckout();
  }, [state, searchParams]);

  const goToCheckout = useCallback(() => {
    if (!checkoutUrl) {
      // Should not happen since the button is disabled until the URL is set
      // eslint-disable-next-line no-alert
      alert('No checkout URL found');
      return;
    }
    if (searchParams.get('new_window')) {
      window.open(checkoutUrl, '_blank');
    } else {
      window.location.href = checkoutUrl;
    }
  }, [searchParams, checkoutUrl]);

  const formattedPrice = useMemo(() => {
    if (!price) return '';
    return Intl.NumberFormat(currencyToIntlLocale(price.currency), {
      style: 'currency',
      currency: price.currency,
    }).format(price.unit_amount / 100);
  }, [price]);

  const error = useMemo(() => priceError || checkoutError, [priceError, checkoutError]);
  const responseError = useMemo(() => error?.response?.data.error || paymentError, [error, paymentError]);

  return (
    <>
      {responseError && isValidErrorNotificationType(responseError) ? <ErrorNotification type={responseError as ErrorNotificationType} /> : <></>}
      <ScreenContainer
        center
        space={`0px ${spacing(6)}px`}
        sx={{
          backgroundColor: colors.lime[5],
        }}
      >
        <Center style={{ width: '100%', height: '100vh' }}>
          <Stack spacing={spacing(12)}>
            <Title align="center" style={{ maxWidth: 500 }}>
              {t('payment_title')}
            </Title>
            <Center>
              {!error && (
                <Paper p="md" style={{ maxWidth: 450, border: '1px solid #444' }}>
                  {isLoading && <Loader />}
                  {!isLoading && price && (
                    <Stack spacing={spacing(4)}>
                      <Text size="xl" weight="bold">
                        {t('subscription_title')}
                      </Text>
                      <Text size="sm">{t('subscription_info')}</Text>
                      <Text size="lg">
                        {formattedPrice} {t('yearly')}
                      </Text>
                      <Button size="md" color="black" onClick={goToCheckout} loading={!checkoutUrl} disabled={!checkoutUrl}>
                        {t('to_payment')}
                      </Button>
                      <Text weight="bold">{t('subscription_features')}</Text>
                      <Text size="sm">
                        <ul style={{ listStyleType: 'none' }}>
                          <Trans
                            i18nKey={'subscription_features_list'}
                            components={{
                              li: <li />,
                            }}
                          />
                        </ul>
                      </Text>
                      <Text size="xs" color={globalTheme.colors.grays[6]} style={{ whiteSpace: 'pre-line', lineHeight: '20px', marginTop: '20px' }}>
                        <span style={{ height: 16, padding: 0, marginRight: 4, verticalAlign: 'text-bottom', lineHeight: '12px' }}>
                          <IconInfo width={16} height={16} fill={globalTheme.colors.grays[6]} />
                        </span>
                        <Trans
                          i18nKey={'hardware_info'}
                          components={{
                            // eslint-disable-next-line
                            a: <a href="mailto:support@carrot.tech" />,
                          }}
                        />
                      </Text>
                    </Stack>
                  )}
                </Paper>
              )}
            </Center>
          </Stack>
        </Center>
      </ScreenContainer>
    </>
  );
};
